/**
 * Modelos genéricos de contrato de prestação de serviço (pt-BR).
 *
 * Cada modelo é uma função pura que recebe os dados do orçamento/cliente/valor
 * e devolve o CORPO do contrato em texto (parágrafos separados por linha em
 * branco). O texto é totalmente editável no formulário antes de enviar para
 * assinatura — os modelos servem só de ponto de partida.
 *
 * Datas em America/Sao_Paulo. Nada aqui promete validade ICP-Brasil: a
 * assinatura remota do app é eletrônica com trilha de evidências
 * (MP 2.200-2 / Lei 14.063/2020).
 *
 * ─── AS DUAS REGRAS DAS CLÁUSULAS PARAMETRIZADAS ────────────────────────────
 *
 * **(1) Cláusula que JÁ EXISTIA continua saindo sempre; só fica mais rica.**
 * Objeto, preço, prazo, obrigações, rescisão e foro são impressos com ou sem
 * dado — quando o campo está vazio, sai a mesma frase genérica de antes. Um
 * formulário sem nenhuma cláusula preenchida produz o texto BYTE A BYTE igual
 * ao de antes desta mudança (#91: o padrão reproduz o passado, e há teste
 * comparando contra o texto capturado antes do refactor).
 *
 * **(2) Cláusula que NÃO EXISTIA só aparece preenchida.** Multa/juros,
 * confidencialidade e multa rescisória entram apenas quando há valor. Imprimir
 * uma cláusula de sigilo com prazo em branco, ou uma multa "a combinar", seria
 * o app inventando termo de negócio que ninguém acordou.
 *
 * ⚠️ **A NUMERAÇÃO É CALCULADA, nunca escrita à mão.** Com cláusula condicional,
 * número chumbado ("CLÁUSULA 5ª") passa a pular ou repetir assim que uma
 * cláusula entra ou sai — e o defeito não dá erro nenhum, só um contrato com
 * duas cláusulas 5ª. Por isso as cláusulas são montadas como LISTA e numeradas
 * no fim.
 */
import {
  EMPTY_CONTRACT_CLAUSES,
  formatClauseDate,
  formatClausePercent,
  pluralizeUnit,
  type ContractClauses,
} from '@/lib/contractClauses';

export interface ContractTemplateInput {
  /** Prestador (empresa que oferece o serviço). */
  providerName: string;
  providerDocument?: string | null;
  providerAddress?: string | null;
  /** Tomador (cliente). */
  clientName: string;
  clientDocument?: string | null;
  clientAddress?: string | null;
  /** Descrição do serviço/objeto. */
  serviceDescription?: string | null;
  /** Valor total do serviço. */
  total?: number | null;
  /** Data-base do contrato (ISO). Default: hoje. */
  date?: string | null;
  /**
   * Cidade do foro/assinatura vinda do CADASTRO da empresa. É o fallback: as
   * cláusulas têm campos próprios (`forumCity`/`signingCity`) que vencem este.
   */
  city?: string | null;
  /** Cláusulas estruturadas. Ausente = todas vazias (comportamento anterior). */
  clauses?: ContractClauses;
}

export interface ContractTemplate {
  id: string;
  label: string;
  description: string;
  build: (input: ContractTemplateInput) => string;
}

const formatBRL = (value?: number | null): string =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    Number.isFinite(value as number) ? (value as number) : 0,
  );

/** Data por extenso em pt-BR (ex.: "22 de julho de 2026"), fuso America/Sao_Paulo. */
const formatLongDate = (iso?: string | null): string => {
  const d = iso ? new Date(iso) : new Date();
  return d.toLocaleDateString('pt-BR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'America/Sao_Paulo',
  });
};

/** Bloco de identificação de uma parte, com placeholders quando faltam dados. */
const partyLine = (name: string, doc?: string | null, address?: string | null): string => {
  const parts = [name?.trim() || '_____________________________'];
  if (doc?.trim()) parts.push(`inscrito(a) no CPF/CNPJ sob nº ${doc.trim()}`);
  if (address?.trim()) parts.push(`com endereço em ${address.trim()}`);
  return parts.join(', ');
};

/** Uma cláusula numerada do contrato. `heading` sem o número — ele é calculado. */
interface Clause {
  heading: string;
  paragraphs: string[];
}

/** Numera as cláusulas na ordem em que sobraram e monta o texto. */
const renderClauses = (clauses: Clause[]): string[] =>
  clauses.flatMap((clause, i) => [
    '',
    `CLÁUSULA ${i + 1}ª — ${clause.heading}`,
    ...clause.paragraphs,
  ]);

/** Trecho de PRAZO a partir das datas; `null` quando nenhuma foi informada. */
const periodPhrase = (clauses: ContractClauses): string | null => {
  const inicio = formatClauseDate(clauses.startDate);
  const fim = formatClauseDate(clauses.endDate);
  if (inicio && fim) return `com início em ${inicio} e término previsto para ${fim}`;
  if (inicio) return `com início em ${inicio}`;
  if (fim) return `com término previsto para ${fim}`;
  return null;
};

/** A cidade do foro: cláusula > cadastro da empresa > lacuna para preencher. */
const forumCityOf = (input: ContractTemplateInput, clauses: ContractClauses): string =>
  clauses.forumCity || input.city?.trim() || '__________________';

/** A cidade da assinatura cai no foro quando não informada (é o caso comum). */
const signingCityOf = (input: ContractTemplateInput, clauses: ContractClauses): string =>
  clauses.signingCity || forumCityOf(input, clauses);

export const CONTRACT_TEMPLATES: ContractTemplate[] = [
  {
    id: 'prestacao_completo',
    label: 'Prestação de serviços (completo)',
    description: 'Contrato com cláusulas de objeto, valor, prazo, obrigações, rescisão e foro.',
    build: (input) => {
      const c = input.clauses ?? EMPTY_CONTRACT_CLAUSES;
      const provider = partyLine(input.providerName, input.providerDocument, input.providerAddress);
      const client = partyLine(input.clientName, input.clientDocument, input.clientAddress);
      const objeto = input.serviceDescription?.trim() || '_____________________________';
      const valor = formatBRL(input.total);
      const foro = forumCityOf(input, c);
      const assinatura = signingCityOf(input, c);
      const dataExtenso = formatLongDate(input.date);

      const clauses: Clause[] = [];

      // 1 · OBJETO — ganha o local de execução quando informado.
      clauses.push({
        heading: 'DO OBJETO',
        paragraphs: [
          `O presente contrato tem por objeto a prestação dos seguintes serviços pelo(a) CONTRATADO(A) ao(à) CONTRATANTE: ${objeto}${
            c.executionPlace ? `, a serem executados em ${c.executionPlace}` : ''
          }.`,
        ],
      });

      // 2 · PREÇO — as condições de pagamento substituem a frase genérica.
      const precoParagrafos = [
        `Pela prestação dos serviços descritos, o(a) CONTRATANTE pagará ao(à) CONTRATADO(A) o valor total de ${valor}, ${
          c.paymentTerms ? `da seguinte forma: ${c.paymentTerms}` : 'na forma e nas datas ajustadas entre as partes'
        }.`,
      ];
      // Multa e juros NÃO existiam: só entram preenchidos (regra 2).
      const multa = formatClausePercent(c.lateFeePercent);
      const juros = formatClausePercent(c.interestPercent);
      if (multa || juros) {
        const penas = [
          multa ? `multa de ${multa}` : null,
          juros ? `juros de ${juros} ao mês` : null,
        ].filter(Boolean);
        precoParagrafos.push(
          `O atraso no pagamento sujeitará o(a) CONTRATANTE a ${penas.join(' e ')} sobre o valor em atraso.`,
        );
      }
      clauses.push({ heading: 'DO PREÇO E DA FORMA DE PAGAMENTO', paragraphs: precoParagrafos });

      // 3 · PRAZO — as datas substituem a frase genérica.
      const periodo = periodPhrase(c);
      clauses.push({
        heading: 'DO PRAZO',
        paragraphs: [
          periodo
            ? `Os serviços serão executados ${periodo}, podendo o prazo ser prorrogado mediante comum acordo, por escrito.`
            : 'Os serviços serão executados no prazo acordado entre as partes, podendo ser prorrogado mediante comum acordo, por escrito.',
        ],
      });

      // 4 e 5 · OBRIGAÇÕES — sem parâmetro.
      clauses.push({
        heading: 'DAS OBRIGAÇÕES DO(A) CONTRATADO(A)',
        paragraphs: [
          'O(A) CONTRATADO(A) obriga-se a executar os serviços com zelo, técnica e qualidade, empregando os materiais e a mão de obra necessários ao bom cumprimento do objeto deste contrato.',
        ],
      });
      clauses.push({
        heading: 'DAS OBRIGAÇÕES DO(A) CONTRATANTE',
        paragraphs: [
          'O(A) CONTRATANTE obriga-se a fornecer as informações e condições necessárias à execução dos serviços e a efetuar os pagamentos nas datas ajustadas.',
        ],
      });

      // · CONFIDENCIALIDADE — cláusula NOVA: só preenchida (regra 2).
      if (c.confidentialityYears != null) {
        clauses.push({
          heading: 'DA CONFIDENCIALIDADE',
          paragraphs: [
            `As partes comprometem-se a manter sigilo sobre quaisquer informações obtidas em razão deste contrato pelo prazo de ${pluralizeUnit(
              c.confidentialityYears,
              'ano',
              'anos',
            )}, contado do seu término.`,
          ],
        });
      }

      // · RESCISÃO — aviso prévio e multa rescisória enriquecem a frase antiga.
      const rescisaoBase = c.noticeDays != null
        ? `O presente contrato poderá ser rescindido por qualquer das partes, mediante aviso prévio por escrito de ${pluralizeUnit(
            c.noticeDays,
            'dia',
            'dias',
          )}, respondendo a parte que der causa à rescisão pelos serviços já executados e pelas perdas e danos a que der causa.`
        : 'O presente contrato poderá ser rescindido por qualquer das partes, mediante comunicação prévia por escrito, respondendo a parte que der causa à rescisão pelos serviços já executados e pelas perdas e danos a que der causa.';
      const rescisaoParagrafos = [rescisaoBase];
      const multaRescisoria = formatClausePercent(c.terminationFeePercent);
      if (multaRescisoria) {
        rescisaoParagrafos.push(
          `A rescisão imotivada sujeitará a parte infratora ao pagamento de multa rescisória de ${multaRescisoria} sobre o valor deste contrato.`,
        );
      }
      clauses.push({ heading: 'DA RESCISÃO', paragraphs: rescisaoParagrafos });

      // · FORO — sempre presente.
      clauses.push({
        heading: 'DO FORO',
        paragraphs: [
          `As partes elegem o foro da comarca de ${foro} para dirimir quaisquer dúvidas oriundas deste contrato, renunciando a qualquer outro, por mais privilegiado que seja.`,
        ],
      });

      return [
        'CONTRATO DE PRESTAÇÃO DE SERVIÇOS',
        '',
        `Pelo presente instrumento particular, de um lado ${provider}, doravante denominado(a) CONTRATADO(A); e, de outro lado, ${client}, doravante denominado(a) CONTRATANTE, têm entre si justo e contratado o seguinte:`,
        ...renderClauses(clauses),
        '',
        `E, por estarem assim justas e contratadas, as partes assinam o presente instrumento, em ${assinatura}, ${dataExtenso}.`,
      ].join('\n');
    },
  },
  {
    id: 'prestacao_simples',
    label: 'Prestação de serviços (simples)',
    description: 'Versão enxuta: objeto, valor e assinatura das partes.',
    build: (input) => {
      const c = input.clauses ?? EMPTY_CONTRACT_CLAUSES;
      const provider = partyLine(input.providerName, input.providerDocument, input.providerAddress);
      const client = partyLine(input.clientName, input.clientDocument, input.clientAddress);
      const objeto = input.serviceDescription?.trim() || '_____________________________';
      const valor = formatBRL(input.total);
      const foro = forumCityOf(input, c);
      const assinatura = signingCityOf(input, c);
      const dataExtenso = formatLongDate(input.date);

      const periodo = periodPhrase(c);
      const avisoPrevio =
        c.noticeDays != null ? `aviso prévio de ${pluralizeUnit(c.noticeDays, 'dia', 'dias')}` : 'aviso prévio';

      // Os corpos SEM número — a numeração é aplicada no fim, pelo índice, para
      // que o item condicional abaixo não obrigue ninguém a renumerar à mão.
      const corpos: string[] = [
        `OBJETO. O(A) CONTRATADO(A) prestará ao(à) CONTRATANTE os seguintes serviços: ${objeto}${
          c.executionPlace ? `, em ${c.executionPlace}` : ''
        }.`,
        `VALOR. Pela prestação dos serviços, o(a) CONTRATANTE pagará o valor total de ${valor}, ${
          c.paymentTerms ? `da seguinte forma: ${c.paymentTerms}` : 'na forma combinada entre as partes'
        }.`,
        `CONDIÇÕES. Os serviços serão executados ${
          periodo ?? 'no prazo e nas condições ajustados entre as partes'
        }. Este contrato pode ser rescindido por qualquer das partes mediante ${avisoPrevio}, respondendo cada uma pelos serviços já prestados.`,
      ];

      // O modelo é enxuto, mas NÃO pode ignorar campo preenchido: um campo que o
      // usuário preenche e o documento não menciona é o "interruptor que não faz
      // nada" do #91c. As cláusulas extras entram condensadas num item só — e o
      // FORO entra aqui, e não como item próprio, porque este modelo nunca teve
      // cláusula de foro: acrescentá-la mudaria a saída do formulário VAZIO.
      const extras = [
        formatClausePercent(c.lateFeePercent) ? `multa de ${formatClausePercent(c.lateFeePercent)} por atraso` : null,
        formatClausePercent(c.interestPercent) ? `juros de ${formatClausePercent(c.interestPercent)} ao mês` : null,
        formatClausePercent(c.terminationFeePercent)
          ? `multa rescisória de ${formatClausePercent(c.terminationFeePercent)}`
          : null,
        c.confidentialityYears != null
          ? `sigilo pelo prazo de ${pluralizeUnit(c.confidentialityYears, 'ano', 'anos')}`
          : null,
        c.forumCity ? `foro da comarca de ${foro}` : null,
      ].filter(Boolean);
      if (extras.length) {
        corpos.push(`DEMAIS CONDIÇÕES. Ficam ajustadas ainda: ${extras.join('; ')}.`);
      }

      const itens = corpos.map((corpo, i) => `${i + 1}. ${corpo}`);

      return [
        'CONTRATO DE PRESTAÇÃO DE SERVIÇOS',
        '',
        `CONTRATADO(A): ${provider}.`,
        `CONTRATANTE: ${client}.`,
        ...itens.flatMap((item) => ['', item]),
        '',
        `E por estarem de acordo, as partes assinam este contrato em ${assinatura}, ${dataExtenso}.`,
      ].join('\n');
    },
  },
];

export const getContractTemplate = (id: string): ContractTemplate | undefined =>
  CONTRACT_TEMPLATES.find((t) => t.id === id);
